import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Scan, ArrowLeft, ArrowUpRight, ShieldCheck, FileCheck, Lock, KeyRound,
  FileSearch, Server,
} from "lucide-react";
import Footer from "../components/Footer";
import { API } from "../lib/api";

const PILLARS = [
  {
    icon: ShieldCheck,
    title: "HIPAA-aligned infrastructure",
    copy: "Administrative, physical and technical safeguards mapped to the HIPAA Security Rule. PHI is isolated, encrypted and access-logged across the entire pipeline.",
  },
  {
    icon: FileCheck,
    title: "BAA support",
    copy: "We execute Business Associate Agreements with covered entities before any PHI flows. Standard BAA available for review during pilot onboarding.",
  },
  {
    icon: FileSearch,
    title: "SOC 2 Type II",
    copy: "Independently audited controls for security, availability and confidentiality — reports available under NDA for procurement teams.",
  },
  {
    icon: Lock,
    title: "Encryption everywhere",
    copy: "AES-256 at rest, TLS 1.3 in transit. Keys are rotated on schedule and managed in dedicated HSM-backed key storage.",
  },
  {
    icon: KeyRound,
    title: "Access control & audit trails",
    copy: "Role-based access, SSO/SAML support, and immutable audit logs recording every study view, export and model inference.",
  },
  {
    icon: Server,
    title: "Deployment flexibility",
    copy: "Single-tenant cloud, VPC peering, or on-premise inference for institutions that require data to never leave their network.",
  },
];

const DATA_ROWS = [
  { k: "Data at rest", v: "AES-256 encrypted volumes, per-tenant key separation" },
  { k: "Data in transit", v: "TLS 1.3 only — DICOM TLS and HTTPS, no plaintext listeners" },
  { k: "PHI handling", v: "Automatic de-identification of DICOM tags before model training; burned-in annotations masked" },
  { k: "Model training", v: "No customer PHI used for model improvement without explicit opt-in consent" },
  { k: "Retention", v: "Customer-defined retention windows with verifiable deletion certificates" },
  { k: "Data residency", v: "US and EU regions available; on-prem option for strict residency mandates" },
];

export default function Compliance() {
  return (
    <div className="bg-ink min-h-screen" data-testid="compliance-page">
      <div className="grain-overlay" aria-hidden="true" />

      <header className="sticky top-0 z-50 bg-white/80 backdrop-blur-xl border-b border-slate-200" data-testid="compliance-navbar">
        <div className="max-w-[1440px] mx-auto px-6 md:px-12 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2" data-testid="compliance-logo">
            <span className="w-8 h-8 bg-brand rounded-lg flex items-center justify-center">
              <Scan className="w-4 h-4 text-white" />
            </span>
            <span className="font-display font-extrabold tracking-tight text-lg">X-GEN.AI</span>
          </Link>
          <div className="flex items-center gap-3">
            <Link
              to="/"
              data-testid="compliance-back-link"
              className="flex items-center gap-1.5 font-mono text-xs uppercase tracking-[0.15em] text-mute hover:text-slate-900 transition-colors duration-300 px-3 py-2"
            >
              <ArrowLeft className="w-3.5 h-3.5" /> Back to site
            </Link>
            <a
              href="/#contact"
              data-testid="compliance-baa-button"
              className="hidden sm:flex items-center gap-1.5 bg-[#0F172A] text-white font-mono text-xs uppercase tracking-[0.15em] px-4 py-2.5 rounded-full hover:bg-brand hover:text-white transition-colors duration-300"
            >
              Request BAA <ArrowUpRight className="w-3.5 h-3.5" />
            </a>
          </div>
        </div>
      </header>

      <main>
        <section className="py-24 md:py-32">
          <div className="max-w-[1440px] mx-auto px-6 md:px-12">
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="font-mono text-xs uppercase tracking-[0.3em] text-brand mb-6"
            >
              Trust & compliance
            </motion.p>
            <motion.h1
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.1 }}
              className="font-display text-4xl sm:text-5xl lg:text-7xl font-black uppercase tracking-tighter leading-[0.95] max-w-5xl"
              data-testid="compliance-headline"
            >
              Engineered for
              <br />
              <span className="text-mute">regulated care.</span>
            </motion.h1>
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.7, delay: 0.25 }}
              className="text-mute text-base md:text-lg leading-relaxed mt-8 max-w-2xl"
            >
              Hospital procurement teams ask hard questions. Here are the straight answers — the controls, audits and agreements behind every X-Gen deployment.
            </motion.p>
          </div>
        </section>

        <section className="border-t border-slate-200 py-24 md:py-32" data-testid="compliance-pillars">
          <div className="max-w-[1440px] mx-auto px-6 md:px-12">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-px bg-slate-200 border border-slate-200 rounded-2xl overflow-hidden">
              {PILLARS.map((p, i) => (
                <motion.div
                  key={p.title}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-60px" }}
                  transition={{ duration: 0.6, delay: (i % 3) * 0.08 }}
                  className="group bg-ink p-8 md:p-10 hover:bg-surface transition-colors duration-500"
                  data-testid={`compliance-pillar-${i}`}
                >
                  <span className="w-11 h-11 border border-slate-200 flex items-center justify-center mb-8 group-hover:border-brand group-hover:bg-brand/10 transition-colors duration-300">
                    <p.icon className="w-5 h-5 text-brand" />
                  </span>
                  <h2 className="font-display text-xl font-semibold tracking-tight mb-3">{p.title}</h2>
                  <p className="text-mute text-sm leading-relaxed">{p.copy}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        <section className="border-t border-slate-200 py-24 md:py-32 bg-surface" data-testid="compliance-data-handling">
          <div className="max-w-[1440px] mx-auto px-6 md:px-12 grid grid-cols-1 md:grid-cols-12 gap-12">
            <div className="md:col-span-4">
              <p className="font-mono text-xs uppercase tracking-[0.3em] text-brand mb-6">Data handling</p>
              <h2 className="font-display text-3xl sm:text-4xl font-bold tracking-tight mb-6">
                Exactly how patient data moves.
              </h2>
              <p className="text-mute text-base leading-relaxed">
                No ambiguity, no fine print. The operational facts your security review will ask for.
              </p>
            </div>
            <div className="md:col-span-8">
              {DATA_ROWS.map((r, i) => (
                <motion.div
                  key={r.k}
                  initial={{ opacity: 0, x: 30 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true, margin: "-60px" }}
                  transition={{ duration: 0.5, delay: i * 0.05 }}
                  className="grid grid-cols-1 sm:grid-cols-12 gap-2 sm:gap-6 py-6 border-t border-slate-200 last:border-b"
                  data-testid={`compliance-data-row-${i}`}
                >
                  <span className="sm:col-span-4 font-mono text-xs uppercase tracking-[0.2em] text-slate-600 pt-0.5">{r.k}</span>
                  <span className="sm:col-span-8 text-mute text-sm leading-relaxed">{r.v}</span>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        <section className="border-t border-slate-200 py-24 md:py-32" data-testid="compliance-regulatory">
          <div className="max-w-[1440px] mx-auto px-6 md:px-12 grid grid-cols-1 md:grid-cols-12 gap-12 items-start">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.7 }}
              className="md:col-span-7"
            >
              <p className="font-mono text-xs uppercase tracking-[0.3em] text-pulse mb-6">Regulatory status</p>
              <h2 className="font-display text-3xl sm:text-4xl font-bold tracking-tight mb-6">
                Transparent about where we stand.
              </h2>
              <p className="text-mute text-base leading-relaxed max-w-xl mb-6">
                X-Gen's oncology detection module is under FDA 510(k) review. Today the platform operates as a clinical decision-support system: it prioritizes worklists and surfaces findings, while every final diagnosis is made and signed by a qualified radiologist.
              </p>
              <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-mute leading-relaxed" data-testid="compliance-disclaimer">
                X-Gen is not intended to replace professional medical judgment. Not for use as the sole basis of diagnosis.
              </p>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.7, delay: 0.15 }}
              className="md:col-span-5 border border-slate-200 bg-ink p-8 md:p-10 rounded-2xl shadow-[0_4px_20px_rgba(15,23,42,0.05)]"
            >
              <h3 className="font-display text-xl font-semibold tracking-tight mb-4">Security review packet</h3>
              <p className="text-mute text-sm leading-relaxed mb-8">
                Architecture diagrams, penetration test summaries, SOC 2 report and our standard BAA — bundled for your infosec team.
              </p>
              <a
                href={`${API}/compliance/packet`}
                download
                data-testid="compliance-request-packet-button"
                className="inline-flex items-center gap-2 bg-brand text-white font-mono text-xs uppercase tracking-[0.15em] px-6 py-4 rounded-full hover:bg-[#0F172A] hover:text-white transition-colors duration-300"
              >
                Download the packet (PDF) <ArrowUpRight className="w-4 h-4" />
              </a>
            </motion.div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}
